// Instance Admin → Edge & HTTPS.
//
// Twasta is the hub: it issues the certificates (or lets Cloudflare), keeps
// them in the platform database, and DELIVERS configuration + certificates to
// every nginx / Caddy server registered on the Servers tab, recording whether
// each delivery arrived. Sub-tabs: Servers (registry), Propagation (what has
// not arrived, per project), Certificates, Delivery & alerts (the scheduler),
// Settings (mode, ACME, Cloudflare). See docs/edge_https.md.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RefreshCw, Upload, FileCode2, Trash2, RotateCcw } from 'lucide-react'
import {
  getEdgeCertificates, getEdgeSettings, previewEdgeConfig,
  reconcileEdge, removeEdgeCertificate, retryEdgeCertificate, testEdgeSettings,
  updateEdgeSettings, uploadEdgeCertificate,
  type EdgeCertificate, type EdgeMode, type EdgeSettings,
} from '@/api/instanceAdmin'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from './adminUi'
import EdgeServersPanel from './EdgeServersPanel'
import { EdgePropagationPanel, EdgeSchedulePanel } from './EdgeDeliveryPanel'

type SubTab = 'servers' | 'propagation' | 'certificates' | 'delivery' | 'settings'
const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'servers', label: 'Servers' },
  { id: 'propagation', label: 'Propagation' },
  { id: 'certificates', label: 'Certificates' },
  { id: 'delivery', label: 'Delivery & alerts' },
  { id: 'settings', label: 'Settings' },
]

const MODES: { id: EdgeMode; label: string; detail: string }[] = [
  { id: 'external', label: 'External', detail: 'Something outside Twasta handles HTTPS. The platform issues no certificates (the behaviour before this screen existed).' },
  { id: 'nginx', label: 'nginx', detail: 'Twasta gets Let’s Encrypt certificates and the edge agent writes the nginx configuration on every server.' },
  { id: 'caddy', label: 'Caddy', detail: 'Same certificates, served by Caddy. The agent loads the configuration through Caddy’s admin API.' },
  { id: 'cloudflare', label: 'Cloudflare', detail: 'Cloudflare for SaaS issues each domain’s certificate at its edge. Servers run nginx with a Cloudflare Origin certificate.' },
]

const STATUS_COLOR: Record<string, string> = {
  active: 'var(--color-success)',
  pending: 'var(--color-text-secondary)',
  issuing: 'var(--color-info)',
  failed: 'var(--color-error)',
  revoked: 'var(--color-text-faint)',
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString() } catch { return iso }
}

function directoryChoice(value: string): 'production' | 'staging' | 'custom' {
  return value === 'production' || value === 'staging' ? value : 'custom'
}

export default function EdgeTab() {
  const [s, setS] = useState<EdgeSettings | null>(null)
  const [form, setForm] = useState({
    mode: 'external' as EdgeMode, acme_email: '', acme_directory: 'production',
    extra_hostnames: '', upstream: '', max_body_mb: 100,
    cloudflare_zone_id: '', cloudflare_token: '', cloudflare_cname_target: '',
    auto_trust: true,
    serve_platform_host: true,
  })
  const [certs, setCerts] = useState<EdgeCertificate[]>([])
  const [sub, setSub] = useState<SubTab>('servers')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [preview, setPreview] = useState<{ engine: string; config: string; skipped: string[] } | null>(null)

  const adopt = (v: EdgeSettings) => {
    setS(v)
    setForm({
      mode: v.mode, acme_email: v.acme_email, acme_directory: v.acme_directory || 'production',
      extra_hostnames: v.extra_hostnames, upstream: v.upstream, max_body_mb: v.max_body_mb,
      cloudflare_zone_id: v.cloudflare_zone_id, cloudflare_token: '',
      cloudflare_cname_target: v.cloudflare_cname_target,
      auto_trust: v.auto_trust,
      serve_platform_host: v.serve_platform_host,
    })
  }

  const refreshLists = useCallback(async () => {
    try {
      setCerts(await getEdgeCertificates())
    } catch (e) {
      toast.error(errText(e, 'Failed to load certificates'))
    }
  }, [])

  useEffect(() => {
    Promise.all([getEdgeSettings().then(adopt), refreshLists()])
      .catch((e) => toast.error(errText(e, 'Failed to load the edge settings')))
      .finally(() => setLoading(false))
    // Servers report every few seconds and certificates change in the
    // background; keep the tables honest without a manual refresh.
    const t = window.setInterval(() => { void refreshLists() }, 15000)
    return () => window.clearInterval(t)
  }, [refreshLists])

  if (loading || !s) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v })); setResult(null)
  }
  const acme = form.mode === 'nginx' || form.mode === 'caddy'
  const dirChoice = directoryChoice(form.acme_directory)

  const save = async () => {
    if (acme && !form.acme_email.trim()) {
      toast.error('An ACME contact email is required — Let’s Encrypt sends expiry warnings to it.')
      return
    }
    setBusy('save'); setResult(null)
    try {
      adopt(await updateEdgeSettings({
        mode: form.mode, acme_email: form.acme_email, acme_directory: form.acme_directory,
        extra_hostnames: form.extra_hostnames, upstream: form.upstream,
        max_body_mb: Number(form.max_body_mb) || 100,
        cloudflare_zone_id: form.cloudflare_zone_id,
        cloudflare_cname_target: form.cloudflare_cname_target,
        auto_trust: form.auto_trust,
        serve_platform_host: form.serve_platform_host,
        ...(form.cloudflare_token.trim() ? { cloudflare_token: form.cloudflare_token.trim() } : {}),
      }))
      toast.success('Edge settings saved')
      void refreshLists()
    } catch (e) {
      toast.error(errText(e, 'Failed to save'))
    } finally { setBusy('') }
  }

  const test = async () => {
    setBusy('test'); setResult(null)
    try {
      const r = await testEdgeSettings()
      const msg = r.mode === 'cloudflare'
        ? `Cloudflare zone ${String(r.zone ?? '')} is readable (${String(r.status ?? '')}).`
        : `Registered with ${String(r.directory ?? '')}.`
      setResult({ ok: true, message: msg })
      adopt(await getEdgeSettings())
    } catch (e) {
      setResult({ ok: false, message: errText(e, 'Check failed') })
    } finally { setBusy('') }
  }

  const openPreview = async (engine?: 'nginx' | 'caddy') => {
    setBusy('preview')
    try { setPreview(await previewEdgeConfig(engine)) } catch (e) {
      toast.error(errText(e, 'Could not render the configuration'))
    } finally { setBusy('') }
  }

  const reconcile = async () => {
    setBusy('reconcile')
    try {
      await reconcileEdge()
      toast.success('Checked which certificates are needed')
      await refreshLists()
    } catch (e) { toast.error(errText(e, 'Reconcile failed')) } finally { setBusy('') }
  }

  const retry = async (c: EdgeCertificate) => {
    try {
      await retryEdgeCertificate(c.id)
      toast.success('Queued — it will be attempted within a minute')
      await refreshLists()
    } catch (e) { toast.error(errText(e, 'Retry failed')) }
  }

  const remove = async (c: EdgeCertificate) => {
    if (!window.confirm(`Stop serving the certificate for ${c.hostnames.join(', ')}?`)) return
    try { await removeEdgeCertificate(c.id); await refreshLists() } catch (e) {
      toast.error(errText(e, 'Remove failed'))
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: 1080 }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
        {SUB_TABS.map((t) => (
          <button
            key={t.id} type="button" onClick={() => setSub(t.id)}
            style={{
              padding: '0.4rem 0.8rem', fontSize: '0.875rem', cursor: 'pointer',
              background: 'transparent', border: 'none',
              borderBottom: `2px solid ${sub === t.id ? 'var(--color-accent)' : 'transparent'}`,
              color: sub === t.id ? 'var(--color-text)' : 'var(--color-text-secondary)',
              fontWeight: sub === t.id ? 600 : 400,
            }}
          >{t.label}</button>
        ))}
      </div>

      {sub === 'servers' && (
        <>
          <EdgeServersPanel mode={s.mode} />
          <SetupGuide settings={s} />
        </>
      )}
      {sub === 'propagation' && <EdgePropagationPanel />}
      {sub === 'delivery' && <EdgeSchedulePanel />}

      {sub === 'settings' && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 640 }}>
        <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>HTTPS edge</h3>
        <InfoBox>
          A domain verified on an application&apos;s <strong>Domains</strong> screen needs a
          certificate before browsers will open it over HTTPS. Here you choose what serves
          HTTPS. In <strong>nginx</strong> or <strong>Caddy</strong> mode, Twasta gets every
          certificate from Let&apos;s Encrypt, keeps it in the platform database and renews it
          30 days before it expires. Twasta then delivers it to every server registered on the
          <strong> Servers</strong> tab, so every server behind a load balancer serves the same
          certificates, and records whether each delivery arrived.
        </InfoBox>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.6rem' }}>
          {MODES.map((m) => (
            <button
              key={m.id} type="button" onClick={() => set('mode', m.id)}
              style={{
                textAlign: 'left', padding: '0.7rem 0.8rem', borderRadius: 8, cursor: 'pointer',
                background: form.mode === m.id ? 'var(--color-accent-weak)' : 'var(--color-bg-secondary)',
                border: `1px solid ${form.mode === m.id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                color: 'var(--color-text)',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{m.label}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                {m.detail}
              </div>
            </button>
          ))}
        </div>

        {acme && (
          <>
            <Field label="ACME contact email" hint="Let's Encrypt sends expiry and policy notices here.">
              <input type="email" value={form.acme_email} onChange={(e) => set('acme_email', e.target.value)}
                placeholder="ops@yourcompany.com" style={{ width: '100%' }} />
            </Field>
            <Field label="Certificate authority" hint={s.acme_registered ? 'An account is registered with this directory.' : 'The account is registered on Test or on the first certificate.'}>
              <select
                value={dirChoice}
                onChange={(e) => set('acme_directory', e.target.value === 'custom' ? 'https://' : e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="production">Let&apos;s Encrypt — production (trusted by browsers)</option>
                <option value="staging">Let&apos;s Encrypt — staging (untrusted, for testing; generous limits)</option>
                <option value="custom">Another ACME directory…</option>
              </select>
              {dirChoice === 'custom' && (
                <input value={form.acme_directory} onChange={(e) => set('acme_directory', e.target.value)}
                  placeholder="https://acme.example.com/directory" style={{ width: '100%', marginTop: 6 }} />
              )}
            </Field>
          </>
        )}

        {form.mode === 'cloudflare' && (
          <>
            <Field label="Cloudflare zone ID" hint="The zone that holds your SaaS custom hostnames (Cloudflare → zone → Overview).">
              <input value={form.cloudflare_zone_id} onChange={(e) => set('cloudflare_zone_id', e.target.value)}
                spellCheck={false} style={{ width: '100%' }} />
            </Field>
            <Field label={`API token ${s.has_cloudflare_token ? '(configured — leave blank to keep)' : '(not set)'}`}
              hint="Needs Zone → SSL and Certificates → Edit on that zone. Encrypted at rest, never shown again.">
              <input type="password" value={form.cloudflare_token} onChange={(e) => set('cloudflare_token', e.target.value)}
                placeholder={s.has_cloudflare_token ? '••••••••' : ''} autoComplete="new-password" spellCheck={false}
                style={{ width: '100%' }} />
            </Field>
            <Field label="CNAME target for customers" hint="The fallback-origin name customers point their domain at (e.g. customers.yourplatform.com). The Domains screen shows this as the record to create.">
              <input value={form.cloudflare_cname_target} onChange={(e) => set('cloudflare_cname_target', e.target.value)}
                spellCheck={false} style={{ width: '100%' }} />
            </Field>
          </>
        )}

        {form.mode !== 'external' && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Advanced
            </summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.75rem' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={form.serve_platform_host}
                  onChange={(e) => set('serve_platform_host', e.target.checked)} />
                <span>
                  Also serve the platform address <code>{s.frontend_host || 'FRONTEND_URL'}</code> itself.
                  Turn this off when a hand-written nginx site keeps serving the platform. That site may
                  route paths this configuration doesn&apos;t, and the two would collide. Project addresses and
                  customer domains are served either way.
                </span>
              </label>
              <Field label="Platform hostnames" hint={`Included: ${s.platform_hostnames.join(', ') || '(none — FRONTEND_URL is local)'}. Add other names the platform itself answers on, comma-separated.`}>
                <input value={form.extra_hostnames} onChange={(e) => set('extra_hostnames', e.target.value)}
                  placeholder="app.yourcompany.com" spellCheck={false} style={{ width: '100%' }} />
              </Field>
              <Field label="Backend address" hint="Where each server's web server forwards requests. Blank = 127.0.0.1:7077.">
                <input value={form.upstream} onChange={(e) => set('upstream', e.target.value)}
                  placeholder="127.0.0.1:7077" spellCheck={false} style={{ width: '100%' }} />
              </Field>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={form.auto_trust} onChange={(e) => set('auto_trust', e.target.checked)} />
                <span>
                  Trust registered web servers automatically. The backend reads the real hostname
                  and visitor address from them, so <code>TRUSTED_PROXY_IPS</code> isn&apos;t needed.
                  {s.trusted_proxy_ips && <> Also trusted from .env: <code>{s.trusted_proxy_ips}</code>.</>}
                </span>
              </label>
              <Field label="Largest upload (MB)">
                <input type="number" min={1} max={10240} value={form.max_body_mb}
                  onChange={(e) => set('max_body_mb', Number(e.target.value))} style={{ width: 140 }} />
              </Field>
            </div>
          </details>
        )}

        {result && (
          <div style={{
            padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.9125rem',
            background: result.ok ? 'var(--color-success-weak)' : 'var(--color-error-weak)',
            color: result.ok ? 'var(--color-success)' : 'var(--color-error)',
          }}>{result.message}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={save} disabled={!!busy}>
            {busy === 'save' ? 'Saving…' : 'Save edge settings'}
          </button>
          <button className="btn btn-secondary" onClick={test} disabled={!!busy || s.mode === 'external'}
            title={s.mode === 'external' ? 'Save a mode first' : 'Uses the SAVED settings'}>
            {busy === 'test' ? 'Checking…' : 'Test'}
          </button>
          <button className="btn btn-secondary" onClick={() => openPreview()} disabled={!!busy}>
            <FileCode2 size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Preview configuration
          </button>
        </div>
      </div>
      )}

      {/* ── certificates ────────────────────────────────────────────────── */}
      {sub === 'certificates' && (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: '1.0625rem', margin: 0 }}>Certificates</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setUploadOpen(true)}>
            <Upload size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />Upload certificate
          </button>
          {acme && (
            <button className="btn btn-secondary btn-sm" onClick={reconcile} disabled={!!busy}
              title="Create requests for any verified domain that has no certificate yet">
              <RefreshCw size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              {busy === 'reconcile' ? 'Checking…' : 'Check domains now'}
            </button>
          )}
        </div>
        {certs.length === 0 ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            {acme
              ? 'None yet. A certificate is requested automatically for every verified domain and platform hostname.'
              : 'None. Upload a wildcard certificate for the platform’s own zone, or a Cloudflare Origin certificate in Cloudflare mode.'}
          </p>
        ) : (
          <table style={tableStyle}>
            <thead><tr><Th>Hostnames</Th><Th>Source</Th><Th>Status</Th><Th>Expires</Th><Th /></tr></thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id}>
                  <Td>
                    {c.hostnames.join(', ')}
                    {c.last_error && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-error)', marginTop: 2 }}>
                        {c.last_error}
                        {c.next_attempt_at && <span style={{ color: 'var(--color-text-faint)' }}> · next try {fmtDate(c.next_attempt_at)}</span>}
                      </div>
                    )}
                  </Td>
                  <Td>{c.source === 'acme' ? 'Let’s Encrypt' : c.source === 'cloudflare_origin' ? 'Cloudflare Origin' : 'Uploaded'}</Td>
                  <Td><span style={{ ...chipStyle, color: STATUS_COLOR[c.status] }}>{c.status}</span></Td>
                  <Td>
                    {c.not_after ? fmtDate(c.not_after) : '—'}
                    {c.days_left !== null && (
                      <div style={{ fontSize: '0.8125rem', color: c.days_left < 14 ? 'var(--color-warning)' : 'var(--color-text-faint)' }}>
                        {c.days_left} days left
                      </div>
                    )}
                  </Td>
                  <Td right>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {c.source === 'acme' && c.status !== 'issuing' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => void retry(c)} title="Try again now">
                          <RotateCcw size={13} />
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => void remove(c)} title="Stop serving">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {uploadOpen && (
        <UploadModal
          cloudflare={form.mode === 'cloudflare'}
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); void refreshLists() }}
        />
      )}
      {preview && (
        <AdminModal title={`Generated ${preview.engine} configuration`} onClose={() => setPreview(null)} wide>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => openPreview('nginx')}>nginx</button>
            <button className="btn btn-secondary btn-sm" onClick={() => openPreview('caddy')}>Caddy</button>
            <button className="btn btn-secondary btn-sm" onClick={() => {
              void navigator.clipboard.writeText(preview.config).then(() => toast.success('Copied'))
            }}>Copy</button>
          </div>
          {preview.skipped.length > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-warning)' }}>
              Left out (not a valid hostname): {preview.skipped.join(', ')}
            </p>
          )}
          <pre style={{
            fontSize: '0.8125rem', background: 'var(--color-bg-secondary)', padding: '0.75rem',
            borderRadius: 6, maxHeight: '55vh', overflow: 'auto', whiteSpace: 'pre',
          }}>{preview.config}</pre>
          <div className="modal-footer" style={{ marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={() => setPreview(null)}>Close</button>
          </div>
        </AdminModal>
      )}
    </div>
  )
}

function UploadModal({ cloudflare, onClose, onDone }: {
  cloudflare: boolean; onClose: () => void; onDone: () => void
}) {
  const [cert, setCert] = useState('')
  const [key, setKey] = useState('')
  const [chain, setChain] = useState('')
  const [source, setSource] = useState<'upload' | 'cloudflare_origin'>(cloudflare ? 'cloudflare_origin' : 'upload')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      const c = await uploadEdgeCertificate({ cert_pem: cert, key_pem: key, chain_pem: chain, source })
      toast.success(`Stored — covers ${c.hostnames.join(', ')}`)
      onDone()
    } catch (e) {
      toast.error(errText(e, 'Upload failed'))
    } finally { setBusy(false) }
  }
  const area = { width: '100%', minHeight: 110, fontFamily: 'monospace', fontSize: '0.8125rem' }
  return (
    <AdminModal title="Upload a certificate" onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <Field label="Kind">
          <select value={source} onChange={(e) => setSource(e.target.value as 'upload' | 'cloudflare_origin')} style={{ width: '100%' }}>
            <option value="upload">Browser-trusted certificate (e.g. a wildcard for the platform zone)</option>
            <option value="cloudflare_origin">Cloudflare Origin CA certificate (only used in Cloudflare mode)</option>
          </select>
        </Field>
        <Field label="Certificate (PEM — a full chain is fine)">
          <textarea value={cert} onChange={(e) => setCert(e.target.value)} spellCheck={false} style={area}
            placeholder="-----BEGIN CERTIFICATE-----" />
        </Field>
        <Field label="Private key (PEM, unencrypted)" hint="Encrypted at rest; only the edge agent decrypts it, to write it 0600 on each server.">
          <textarea value={key} onChange={(e) => setKey(e.target.value)} spellCheck={false} style={area}
            placeholder="-----BEGIN PRIVATE KEY-----" />
        </Field>
        <Field label="Intermediate chain (optional)">
          <textarea value={chain} onChange={(e) => setChain(e.target.value)} spellCheck={false} style={{ ...area, minHeight: 70 }} />
        </Field>
      </div>
      <div className="modal-footer" style={{ marginTop: 12 }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy || !cert.trim() || !key.trim()}>
          {busy ? 'Checking…' : 'Upload'}
        </button>
      </div>
    </AdminModal>
  )
}

function SetupGuide({ settings }: { settings: EdgeSettings }) {
  const mono = { fontFamily: 'monospace', fontSize: '0.8125rem' }
  return (
    <details>
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.9625rem' }}>
        Clustering behind an Oracle OCI load balancer
      </summary>
      <div style={{ marginTop: '0.75rem', maxWidth: 760 }}>
        <InfoBox>
          <ol style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <li>
              Create an <strong>OCI Network Load Balancer</strong> (not the HTTP one). Add listeners
              <strong> TCP 80</strong> and <strong>TCP 443</strong> forwarding to the same ports on every
              application server, and turn on <strong>Preserve source IP</strong>. Encrypted traffic passes
              through untouched, so adding a domain never needs a load-balancer change.
            </li>
            <li>
              Health check: <span style={mono}>HTTP · port 7077 · path /api/health</span> (or TCP 443 if
              7077 is not reachable from the load balancer subnet).
            </li>
            <li>
              Security lists / NSGs: allow 80 and 443 from <span style={mono}>0.0.0.0/0</span> to the
              servers — Let&apos;s Encrypt validates over port 80.
            </li>
            <li>
              Register every nginx/Caddy server above and install the agent on it. The web servers
              can be separate machines from the Twasta application servers. The Twasta
              application servers must share the same database and the same projects storage
              (<span style={mono}>~/.twasta/projects</span> on OCI File Storage / NFS).
            </li>
            <li>
              Point the platform hostname{settings.platform_hostnames.length > 1 ? 's' : ''}
              {' '}({settings.platform_hostnames.join(', ') || 'FRONTEND_URL'}) at the load balancer&apos;s
              public IP. That IP is what the Domains screen shows customers as the A record.
            </li>
            <li>
              <span style={mono}>TRUSTED_PROXY_IPS</span> in <span style={mono}>backend/.env</span> must
              name the web servers (<span style={mono}>127.0.0.1</span> when nginx runs on the same machine,
              otherwise their private IPs or subnet), so the backend reads the real hostname and client
              address. Currently: <span style={mono}>{settings.trusted_proxy_ips || '(not set)'}</span>.
            </li>
          </ol>
        </InfoBox>
      </div>
    </details>
  )
}
